import { sql } from "drizzle-orm";
import { YouTubeMediaDownloadFormat } from "../schema/youtube-media.js";
import { pgSchema, uniqueIndex } from "drizzle-orm/pg-core";
import type { PgColumnsBuilders as ColumnBuilders } from "drizzle-orm/pg-core/columns/all";

function identifiable(t: ColumnBuilders) {
  return {
    /** This the unique identifier to a specific entity of this type */
    id: t.uuid().notNull().primaryKey().defaultRandom(),
  };
}

function timestamps(t: ColumnBuilders) {
  return {
    /** When was this record created? */
    createdAt: t.timestamp("created_at").notNull().defaultNow(),

    /** When was the record last updated? Defaults to `createdAt` on first save */
    updatedAt: t.timestamp("updated_at").notNull().defaultNow(),
  };
}

/**
 * The `iam` schema is primarily to encapsulate all models that represent
 * different identities and how they acceess other records or features of the
 * application.
 */
export const iam = pgSchema("iam");

export const sessions = iam.table("session", (t) => ({
  ...identifiable(t),
  ...timestamps(t),

  lastAccessToken: t.varchar().notNull(),

  sourceSignInTokenId: t.uuid().references(() => signInTokens.id),

  userId: t
    .uuid()
    .references(() => users.id)
    .notNull(),
}));

export const signInTokens = iam.table("sign_in_tokens", (t) => ({
  ...identifiable(t),

  /**
   * When did or will the token expire?
   *
   * If the timestamp is in the past, the token can no longer be used and can be
   * safely destroyed.
   */
  expiresAt: t.timestamp("expires_at").notNull(),

  /**
   * When was the token issued?
   *
   * If the timestamp is in the future, the token cannot be used yet.
   */
  issuedAt: t.timestamp("issued_at").notNull(),

  userId: t
    .uuid()
    .references(() => users.id)
    .notNull(),

  /** A URL-safe string-encoded form of a token that a user can provide */
  value: t.varchar({ length: 1024 }).notNull().unique(),
}));

export const users = iam.table("users", (t) => ({
  ...identifiable(t),
  ...timestamps(t),

  /** The user's primary email address used for autuhentication */
  emailAddress: t.varchar("email_address", { length: 1024 }).notNull().unique(),
}));

/**
 * The `accounting` schema encapsulates the entities like accounts and
 * transactions.
 */
export const accounting = pgSchema("accounting");
export const accounts = accounting.table("accounts", (t) => ({
  ...identifiable(t),
  ...timestamps(t),

  /**
   * Who created the account? If this is null, the account was created by the
   * Heliopolis application.
   */
  creatorId: t.uuid("creator_id").references(() => users.id),

  /**
   * The human-readable name of the account
   * @example "RBC savings acct"
   */
  name: t.varchar("name", { length: 1024 }).notNull(),

  /**
   * Raw JSON data from which this account was imported
   *
   * The shape of this JSON data is not static. Zod schemas should be used to
   * ensure the data has the expected shape.
   */
  importMeta: t.jsonb("import_meta"),
}));

export const transfers = accounting.table("transfers", (t) => ({
  ...identifiable(t),
  ...timestamps(t),

  /**
   * This is a short (typically half-line long) one-liner to let someone
   * understand why this transfer probably happened.
   *
   * @example "Payment for monthly subscription to streaming service"
   */
  description: t.varchar().notNull(),

  sourceAccountId: t.uuid("source_account_id").references(() => accounts.id),
  withdrawnAt: t.timestamp("withdrawn_at").notNull(),

  destinationAccountId: t.uuid("destination_account_id").references(() => accounts.id),
  depositedAt: t.timestamp("deposited_at").notNull(),

  /**
   * What amount was transferred? Integers should be preferred so use cents and
   * calculate dollars during presentation.
   *
   * @example 100 // one dollar
   */
  amount: t.numeric({ mode: "bigint" }),
}));

/**
 * The `media` schema contains user-requested media acquisitions and their
 * durable background-work records.
 */
export const media = pgSchema("media");

export const mediaProvider = media.enum("provider", ["youtube"]);
export const mediaRequestedFormat = media.enum("requested_format", YouTubeMediaDownloadFormat.enum);
export const mediaTaskOperation = media.enum("task_operation", ["delete", "fetch"]);
export const mediaTaskState = media.enum("task_state", [
  "cancelled",
  "failed",
  "queued",
  "running",
  "succeeded",
]);
export const mediaTaskPhase = media.enum("task_phase", [
  "deleting",
  "downloading",
  "uploading",
  "validating",
]);

export const mediaRequests = media.table("media_requests", (t) => ({
  ...identifiable(t),
  ...timestamps(t),

  ownerId: t
    .uuid("owner_id")
    .references(() => users.id)
    .notNull(),

  sourceUrl: t.varchar("source_url", { length: 2048 }).notNull(),
  provider: mediaProvider().notNull(),
  requestedFormat: mediaRequestedFormat("requested_format").notNull(),

  sourceTitle: t.varchar("source_title", { length: 1024 }),
  currentObjectKey: t.varchar("current_object_key", { length: 1024 }),
  fileName: t.varchar("file_name", { length: 1024 }),
  contentType: t.varchar("content_type", { length: 255 }),
  byteSize: t.bigint("byte_size", { mode: "number" }),
  durationSeconds: t.integer("duration_seconds"),

  deletionRequestedAt: t.timestamp("deletion_requested_at"),
}));

export const mediaTasks = media.table(
  "media_tasks",
  (t) => ({
    ...identifiable(t),
    ...timestamps(t),

    mediaRequestId: t
      .uuid("media_request_id")
      .references(() => mediaRequests.id, { onDelete: "cascade" })
      .notNull(),

    operation: mediaTaskOperation().notNull(),
    state: mediaTaskState().notNull().default("queued"),
    phase: mediaTaskPhase(),
    progressPercent: t.integer("progress_percent"),
    downloadedBytes: t.bigint("downloaded_bytes", { mode: "number" }),
    totalBytes: t.bigint("total_bytes", { mode: "number" }),
    cancellationRequestedAt: t.timestamp("cancellation_requested_at"),
    failureCode: t.varchar("failure_code", { length: 255 }),
    failureMessage: t.text("failure_message"),
    storageObjectKey: t.varchar("storage_object_key", { length: 1024 }),
    startedAt: t.timestamp("started_at"),
    finishedAt: t.timestamp("finished_at"),
  }),
  (table) => [
    uniqueIndex("media_tasks_active_fetch_per_request")
      .on(table.mediaRequestId)
      .where(sql`${table.operation} = 'fetch' and ${table.state} in ('queued', 'running')`),
    uniqueIndex("media_tasks_active_delete_per_request")
      .on(table.mediaRequestId)
      .where(sql`${table.operation} = 'delete' and ${table.state} in ('queued', 'running')`),
  ],
);
